/**
 * Sequencing OAuth Clients — super-admin only.
 *
 * Lists every registered Sequencing Live App OAuth client, lets a
 * super-admin register new ones (returning the plaintext secret
 * **exactly once**), and revokes existing clients.
 *
 * Endpoints used:
 *   GET    /api/v1/sequencing-portal/admin/clients
 *   POST   /api/v1/sequencing-portal/admin/clients
 *   DELETE /api/v1/sequencing-portal/admin/clients/{client_id}
 */
import { useEffect, useState } from "react";
import {
  Key, Plus, Copy, Trash, ShieldWarning, ArrowsClockwise,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { createPortal } from "react-dom";
import AdminPageHeader from "@/components/AdminPageHeader";
import api from "@/lib/api";

function NewClientModal({ initial, onClose, onCreated }) {
  const [name, setName] = useState(initial?.name || "");
  const [redirectUrisRaw, setRedirectUrisRaw] = useState(
    (initial?.redirect_uris || []).join("\n"),
  );
  const [customId, setCustomId] = useState(initial?.client_id || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const uris = redirectUrisRaw
      .split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    if (!name.trim()) return toast.error("Name required");
    if (uris.length === 0) return toast.error("At least one redirect URI required");
    setBusy(true);
    try {
      const body = { name: name.trim(), redirect_uris: uris };
      if (customId.trim()) body.client_id = customId.trim();
      const r = await api.post("/v1/sequencing-portal/admin/clients", body);
      onCreated(r.data);    // parent shows the one-shot secret screen
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-3"
         data-testid="oauth-new-client-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white rounded-sm shadow-xl border border-slate-200 overflow-hidden flex flex-col">
        <header className="px-4 py-3 border-b border-slate-200 flex items-center gap-3">
          <Key size={16} weight="bold" className="text-emerald-700" />
          <h2 className="text-sm font-bold text-slate-900 flex-1">Register OAuth client</h2>
          <button onClick={onClose}
                  className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500">
            <X size={16} />
          </button>
        </header>
        <div className="p-4 space-y-3">
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1">
              Name
            </label>
            <input value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="Sequencing Live App"
                   data-testid="oauth-new-name"
                   className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
          </div>
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1">
              Redirect URIs (one per line)
            </label>
            <textarea value={redirectUrisRaw}
                      onChange={(e) => setRedirectUrisRaw(e.target.value)}
                      data-testid="oauth-new-uris"
                      rows={3}
                      placeholder={"https://live.compressedairportal.com/auth/callback\nhttp://localhost:8080/auth/callback"}
                      className="w-full px-2 py-1.5 text-xs font-mono border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
          </div>
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1">
              Client ID <span className="text-slate-400 font-normal normal-case">(optional — auto-generated if blank)</span>
            </label>
            <input value={customId} onChange={(e) => setCustomId(e.target.value)}
                   placeholder="live-sequencing-app"
                   data-testid="oauth-new-id"
                   className="w-full px-2 py-1.5 text-sm font-mono border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
          </div>
        </div>
        <footer className="px-4 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button onClick={onClose}
                  className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-slate-700 hover:bg-slate-100 rounded-sm">
            Cancel
          </button>
          <button onClick={submit} disabled={busy}
                  data-testid="oauth-new-submit"
                  className="px-4 py-1.5 text-xs font-bold uppercase tracking-wider bg-emerald-700 hover:bg-emerald-800 text-white rounded-sm disabled:opacity-50">
            {busy ? "Registering…" : "Register"}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

function SecretRevealModal({ secret, onClose }) {
  /* Shown EXACTLY ONCE after a successful registration. The plaintext
     secret is gone forever the moment this dialog is dismissed. */
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(secret.client_secret);
      toast.success("Secret copied to clipboard");
    } catch { toast.error("Copy failed"); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-3"
         data-testid="oauth-secret-modal">
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative w-full max-w-lg bg-white rounded-sm shadow-xl border-2 border-amber-400 overflow-hidden flex flex-col">
        <header className="px-4 py-3 bg-amber-50 border-b border-amber-200 flex items-center gap-3">
          <ShieldWarning size={18} weight="bold" className="text-amber-700" />
          <h2 className="text-sm font-bold text-amber-900 flex-1">
            Save this secret — it will never be shown again
          </h2>
        </header>
        <div className="p-4 space-y-3">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
              Client ID
            </label>
            <code className="block px-2 py-1.5 text-xs font-mono bg-slate-50 border border-slate-200 rounded-sm break-all"
                  data-testid="oauth-secret-client-id">
              {secret.client_id}
            </code>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-red-700 mb-1">
              Client Secret (plaintext, shown ONCE)
            </label>
            <code className="block px-2 py-2 text-sm font-mono bg-red-50 border-2 border-red-300 rounded-sm break-all"
                  data-testid="oauth-secret-client-secret">
              {secret.client_secret}
            </code>
          </div>
          <p className="text-[11px] text-slate-600 leading-snug">
            Hand this pair to the external team as
            <code className="mx-1">LIVE_APP_CLIENT_ID</code>+
            <code className="mx-1">LIVE_APP_CLIENT_SECRET</code>.
            The server only stores the hash — there is no way to
            recover the plaintext later.
          </p>
        </div>
        <footer className="px-4 py-3 border-t border-slate-200 flex items-center justify-between gap-2">
          <button onClick={copy} data-testid="oauth-secret-copy"
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase tracking-wider bg-slate-100 hover:bg-slate-200 rounded-sm">
            <Copy size={12} weight="bold" /> Copy secret
          </button>
          <button onClick={onClose} data-testid="oauth-secret-done"
                  className="px-4 py-1.5 text-xs font-bold uppercase tracking-wider bg-emerald-700 hover:bg-emerald-800 text-white rounded-sm">
            I&apos;ve saved it — close
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}



export default function SequencingOAuthClientsPage({ user, onLogout, onLogoutAll }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [reveal, setReveal] = useState(null);  // one-shot secret

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/v1/sequencing-portal/admin/clients");
      setClients(r.data?.clients || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load clients");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const revoke = async (cid) => {
    if (!window.confirm(`Revoke client "${cid}"?\n\nAny Live App deployment using this client_id will stop being able to authenticate users immediately.`)) return;
    try {
      await api.delete(`/v1/sequencing-portal/admin/clients/${encodeURIComponent(cid)}`);
      toast.success("Client revoked");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed");
    }
  };

  const active = clients.filter((c) => !c.revoked_at);
  const revoked = clients.filter((c) => c.revoked_at);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="oauth-clients-page">
      <AdminPageHeader title="Sequencing OAuth Clients"
                       user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} />

      <main className="max-w-4xl mx-auto px-4 md:px-8 py-6 space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-[12px] text-blue-900 leading-snug">
          Register one OAuth client per Sequencing Live App deployment.
          Each registration returns a <b>client secret</b> shown exactly
          once — capture it immediately and hand it to the external
          team. Lost secrets cannot be recovered (only rotated by
          revoke + re-register).
        </div>

        <div className="bg-white border border-slate-200 rounded-sm">
          <div className="px-3 py-2 border-b border-slate-200 flex items-center gap-2">
            <Key size={14} weight="bold" className="text-emerald-700" />
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex-1">
              Active clients
            </h2>
            <span className="text-[10px] font-mono text-slate-500">{active.length}</span>
            <button onClick={load} disabled={loading}
                    data-testid="oauth-refresh"
                    className="text-[10px] uppercase font-bold px-2 py-1 rounded-sm bg-slate-100 hover:bg-slate-200 text-slate-700 inline-flex items-center gap-1">
              <ArrowsClockwise size={10} weight="bold" /> {loading ? "…" : "Refresh"}
            </button>
            <button onClick={() => setShowNew(true)}
                    data-testid="oauth-new-button"
                    className="text-[10px] uppercase font-bold px-2 py-1 rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white inline-flex items-center gap-1">
              <Plus size={10} weight="bold" /> New client
            </button>
          </div>

          {loading ? (
            <div className="p-6 text-center text-[12px] text-slate-500">Loading…</div>
          ) : active.length === 0 ? (
            <div className="p-6 text-center text-[12px] text-slate-500">
              No active clients. Click <b>New client</b> to issue one for the
              Sequencing Live App deployment.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {active.map((c) => (
                <li key={c.client_id} className="px-3 py-2.5 flex items-start gap-3"
                    data-testid={`oauth-row-${c.client_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="text-[13px] font-bold text-slate-800">{c.name}</span>
                      <code className="text-[10px] text-slate-500 font-mono">{c.client_id}</code>
                    </div>
                    {Array.isArray(c.redirect_uris) && c.redirect_uris.length > 0 && (
                      <ul className="mt-0.5 text-[10px] font-mono text-slate-500 space-y-0.5">
                        {c.redirect_uris.map((u, i) => (
                          <li key={i} className="truncate">• {u}</li>
                        ))}
                      </ul>
                    )}
                    <div className="mt-0.5 text-[10px] text-slate-400">
                      created {c.created_at ? new Date(c.created_at).toLocaleString() : "—"}
                      {c.created_by && <> by <span className="font-mono">{c.created_by}</span></>}
                    </div>
                  </div>
                  <button onClick={() => revoke(c.client_id)}
                          data-testid={`oauth-revoke-${c.client_id}`}
                          title="Revoke client"
                          className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-bold uppercase tracking-wider text-red-700 hover:bg-red-50 border border-red-200">
                    <Trash size={10} weight="bold" /> Revoke
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {revoked.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="px-3 py-2 border-b border-slate-200 flex items-center gap-2">
              <X size={14} weight="bold" className="text-slate-500" />
              <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex-1">
                Revoked
              </h2>
              <span className="text-[10px] font-mono text-slate-500">{revoked.length}</span>
            </div>
            <ul className="divide-y divide-slate-100">
              {revoked.map((c) => (
                <li key={c.client_id} className="px-3 py-2 flex items-baseline gap-3 opacity-60">
                  <span className="text-[12px] font-bold text-slate-700">{c.name}</span>
                  <code className="text-[10px] text-slate-500 font-mono">{c.client_id}</code>
                  <span className="ml-auto text-[10px] text-slate-400">
                    revoked {c.revoked_at ? new Date(c.revoked_at).toLocaleString() : "—"}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </main>

      {showNew && (
        <NewClientModal
          onClose={() => setShowNew(false)}
          onCreated={(secret) => {
            setShowNew(false);
            setReveal(secret);
            load();
          }}
        />
      )}
      {reveal && (
        <SecretRevealModal secret={reveal} onClose={() => setReveal(null)} />
      )}
    </div>
  );
}
